import type { ComponentType } from 'react';

import type { TypographyColors } from '@ui/uikit/components/common/Typography';
import { Typography } from '@ui/uikit/components/common/Typography';
import { TrendDown } from '@ui/uikit/components/icons/mono/TrendDown';
import { TrendUp } from '@ui/uikit/components/icons/mono/TrendUp';
import { cn } from '@ui/uikit/utils';

import { PredictionDirection } from '../../../../../../model/PredictionDirection';
import type { PredictionOptionState } from '../PredictionCard/PredictionOptions/predictionOptionState';

type OptionConfigItem = {
  Icon: ComponentType<{ className: string }>;
  states: Record<
    PredictionOptionState,
    {
      titleColor: TypographyColors;
      subtitleColor: TypographyColors;
      borderColor: string;
      borderWidth: number;
      bgColor?: string;
    }
  >;
};

const OptionConfig: Record<PredictionDirection, OptionConfigItem> = {
  [PredictionDirection.UP]: {
    Icon: TrendUp,
    states: {
      default: {
        titleColor: 'green',
        subtitleColor: 'primary',
        borderColor: 'border-base-border-medium',
        borderWidth: 1,
      },
      selected: {
        titleColor: 'green',
        subtitleColor: 'primary',
        borderColor: 'border-base-border-green',
        borderWidth: 2,
      },
      ended: {
        titleColor: 'green',
        subtitleColor: 'primary',
        borderColor: '',
        borderWidth: 0,
        bgColor: 'secondary-green',
      },
      disabled: {
        titleColor: 'tertiary',
        subtitleColor: 'tertiary',
        borderColor: 'border-base-border-medium',
        borderWidth: 1,
      },
    },
  },
  [PredictionDirection.DOWN]: {
    Icon: TrendDown,
    states: {
      default: {
        titleColor: 'red',
        subtitleColor: 'primary',
        borderColor: 'border-base-border-medium',
        borderWidth: 1,
      },
      selected: {
        titleColor: 'red',
        subtitleColor: 'primary',
        borderColor: 'border-base-border-red',
        borderWidth: 2,
      },
      ended: {
        titleColor: 'red',
        subtitleColor: 'primary',
        borderColor: '',
        borderWidth: 0,
        bgColor: 'secondary-red',
      },
      disabled: {
        titleColor: 'tertiary',
        subtitleColor: 'tertiary',
        borderColor: 'border-base-border-medium',
        borderWidth: 1,
      },
    },
  },
};

type PredictionOptionProps = {
  direction: PredictionDirection;
  state: PredictionOptionState;
  value: string;
  onClick?: (direction: PredictionDirection) => void;
};

export const PredictionOption = ({ direction, state, value, onClick }: PredictionOptionProps) => {
  const { Icon, states } = OptionConfig[direction];
  const stateConfig = states[state];
  const isDisabled = state === 'disabled';

  const handleClick = () => {
    if (!onClick || isDisabled) {
      return;
    }

    onClick?.(direction);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex flex-col justify-center items-center w-full gap-0.5 h-16 rounded-xl ',
        `${stateConfig.borderColor}`,
        `border${stateConfig.borderWidth === 1 ? '' : `-${stateConfig.borderWidth}`}`,
        `${stateConfig.bgColor ? `bg-base-bg-${stateConfig.bgColor}` : ''}`,
        onClick && !isDisabled ? 'cursor-pointer' : '',
      )}
    >
      <div className="flex items-center pl-1">
        <Typography color={stateConfig.titleColor} variant="caption-s-strong">
          <div>{direction}</div>
        </Typography>
        <Icon className={`icon-xs text-base-text-${stateConfig.titleColor}`} />
      </div>
      <Typography variant="caption-xs-strong" color={stateConfig.subtitleColor}>
        {value}
      </Typography>
    </div>
  );
};
